import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Dropdown} from 'react-bootstrap'
import {ContactStatus, GetContactParams, Tag} from '../../../../../services/cellcast.v2.types'
import './Filter.scss'

import FilterPopover from './FilterPopover'
import FilterTag from './FilterTag'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useLocation, useParams} from 'react-router-dom'

type FilterProps = {
  tags: Tag[]
  params: GetContactParams
  groupField?: string[]
  setParams: (params: GetContactParams) => void
  doFetchData: () => void
  filterFieldEnable: boolean
}

const Filter: FC<FilterProps> = ({
  tags,
  params,
  setParams,
  doFetchData,
  groupField,
  filterFieldEnable,
}) => {
  const {id: contactListId} = useParams()
  const location = useLocation()
  const [newParamValues, setNewParamValues] = useState<GetContactParams>({...params})
  const [selectedColumn, setSelectedColumn] = useState('')
  const [showFilterPopover, setShowFilterPopover] = useState(false)
  const [isDoFetch, setIsDoFetch] = useState(false)
  const [isParamsUpdated, setIsParamsUpdated] = useState(false)

  // handle specificly to show or hide filter for specific columns (e.g list / status)
  const isShowFilterTags = useMemo(() => {
    const hasInclude = !!newParamValues.tag && newParamValues.tag.trim() !== ''
    const hasExclude = !!newParamValues.excludeTag && newParamValues.excludeTag.trim() !== ''
    return hasInclude || hasExclude
  }, [newParamValues.tag, newParamValues.excludeTag])

  // This ref anchors the FilterPopover to the dropdown toggle.
  const dropdownToggleRef = useRef<HTMLButtonElement | null>(null)

  // Controlled inputs for the popover.
  const [includeTagOrList, setIncludeTagOrList] = useState<Tag[]>([])
  const [excludeTagOrList, setExcludeTagOrList] = useState<Tag[]>([])

  const [status, setStatus] = useState<ContactStatus | undefined>()
  const [containValue, setContainValue] = useState('')

  // Extend available columns.
  const defaultGroupField = useMemo(() => {
    if (!groupField) return []

    let columns = []
    if (filterFieldEnable) {
      for (let i = 0; i < groupField.length; i++) {
        let isDuplicate = false
        for (let j = 0; j < i; j++) {
          if (groupField[i]?.toLowerCase() === groupField[j]?.toLowerCase()) {
            isDuplicate = true
            break
          }
        }
        if (!isDuplicate) {
          columns.push(groupField[i])
        }
      }
    }

    return [...(columns || []), 'Mobile', 'Tags']
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupField])

  const handleResetPopover = () => {
    setIncludeTagOrList([])
    setExcludeTagOrList([])
    setContainValue('')
  }

  useEffect(() => {
    if (isParamsUpdated) {
      const reqParams = {...newParamValues} as any
      
      let mobileFilter = ""
      for (let key in reqParams) {
        if (key.includes('searchFieldsNames')) {
          const value = reqParams[key]
          if (value?.toLowerCase() === 'mobile') {
            const valueKey = key.replace('searchFieldsNames', 'searchFieldsValues')
            const value = reqParams[valueKey]
            if(value && value.length > 0) {
              mobileFilter = value
            }
            break
          }
          break
        }
      }

      if(mobileFilter && mobileFilter.length > 0) {
        reqParams.number = mobileFilter
      }

      setParams({...reqParams})
      setIsDoFetch(true)
    }
  }, [isParamsUpdated, newParamValues, params, setParams])

  useEffect(() => {
    if (isDoFetch) {
      doFetchData()
      setIsDoFetch(false)
      setIsParamsUpdated(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoFetch])

  const handleApplyPopover = () => {
    if (selectedColumn?.toLowerCase() === 'tags' || selectedColumn?.toLowerCase() === 'list') {
      // ✅ Only update if at least one tag is selected
      if (includeTagOrList.length > 0 || excludeTagOrList.length > 0) {
        if (includeTagOrList.length > 0) {
          newParamValues.tag = includeTagOrList.map((tag) => tag._id).join(',') // <-- CSV string
        }
        if (excludeTagOrList.length > 0) {
          newParamValues.excludeTag = excludeTagOrList.map((tag) => tag._id).join(',')
        }
      }
    } else if (selectedColumn?.toLowerCase() === 'status') {
      if (status?.trim()) {
        newParamValues.status = status
      }
    } else {
      // ✅ For other columns, add dynamic keys.
      const indices = Object.keys(newParamValues)
        .filter((key) => key.startsWith('searchFieldsNames['))
        .map((key) => {
          const match = key.match(/\[(\d+)\]/)
          return match ? parseInt(match[1], 10) : -1
        })
      const nextIndex = indices.length > 0 ? Math.max(...indices) + 1 : 0

      newParamValues[`searchFieldsNames[${nextIndex}]`] = selectedColumn
      newParamValues[`searchFieldsValues[${nextIndex}]`] = containValue.trim()
    }

    setNewParamValues({ ...newParamValues })
    setIsParamsUpdated(true)
    setShowFilterPopover(false)
  }

  const handleSelectColumn = (col: string) => {
    setSelectedColumn(col)
    handleResetPopover()
    setShowFilterPopover(true)
  }

  const filterTags = useMemo(() => {
    const filterTag: {
      column: string
      includeText?: string
      excludeText?: string
      value?: string
    }[] = []

    // Handle Tags filter
    if (isShowFilterTags && (newParamValues.tag || newParamValues.excludeTag)) {
      let includeText: string | undefined
      let excludeText: string | undefined

      if (newParamValues.tag) {
        const includeIds = newParamValues.tag.split(',').filter(Boolean)
        includeText = includeIds
          .map((id) => tags.find((t) => t._id === id && t.type === 'tag')?.name)
          .filter(Boolean)
          .join(', ')
      }

      if (newParamValues.excludeTag) {
        const excludeIds = newParamValues.excludeTag.split(',').filter(Boolean)
        excludeText = excludeIds
          .map((id) => tags.find((t) => t._id === id && t.type === 'tag')?.name)
          .filter(Boolean)
          .join(', ')
      }

      filterTag.push({
        column: 'Tags',
        includeText,
        excludeText,
      })
    }

    // Handle Status filter
    if (isShowFilterTags && newParamValues.status) {
      filterTag.push({column: 'Status', value: String(newParamValues.status)})
    }

    // Handle dynamic search fields by iterating over keys that match "searchFieldsNames[<number>]"
    Object.keys(newParamValues)
      .filter((key) => key.startsWith('searchFieldsNames['))
      .forEach((key) => {
        const match = key.match(/searchFieldsNames\[(\d+)\]/)
        if (match) {
          const index = match[1]
          const fieldName = (newParamValues as any)[key]
          const valueKey = `searchFieldsValues[${index}]` as keyof GetContactParams
          const fieldValue = newParamValues[valueKey] as string
          if (fieldName) {
            filterTag.push({
              column: fieldName,
              value: fieldValue,
            })
          }
        }
      })

    return filterTag
  }, [isShowFilterTags, newParamValues, tags])

  const removeFilter = (tag: {
    column: string
    includeText?: string
    excludeText?: string
    value?: string
  }) => {
    if (tag.column?.toLowerCase() === 'tags') {
      if (location.pathname.includes('list') && contactListId) {
        newParamValues.tag = contactListId
      } else {
        newParamValues.tag = undefined
      }
      newParamValues.excludeTag = undefined
    } else if (tag.column?.toLowerCase() === 'status') {
      newParamValues.status = undefined
    } else {
      // Handle dynamic search fields
      const keys = Object.keys(newParamValues)
        .filter((key) => key.startsWith('searchFieldsNames[')) // Find all keys that match the pattern
        .map((key) => {
          const match = key.match(/searchFieldsNames\[(\d+)\]/)
          return match ? {index: match[1], key} : null
        })
        .filter(Boolean) as {index: string; key: string}[]

      const entry = keys.find(({key}) => (newParamValues as any)[key] === tag.column)
      if (entry) {
        const {index} = entry
        delete newParamValues[`searchFieldsNames[${index}]` as keyof GetContactParams]
        delete newParamValues[`searchFieldsValues[${index}]` as keyof GetContactParams]
      }
    }

    setNewParamValues({...newParamValues})
    setIsParamsUpdated(true)
  }

  return (
    <div className='row mb-5'>
      <div className='col-12'>
        {/* Container with the dropdown and the filter filterTag */}
        <div className='input-tags-container d-flex align-items-center'>
          {/* Dropdown button */}
          <Dropdown className='me-3'>
            <Dropdown.Toggle className='dropdown-button' ref={dropdownToggleRef} variant='light'>
              <KTSVG
                path='/media/icons/duotune/new-added/minus-squared.svg'
                className='svg-icon-1'
              />
              <span className='ms-2 text-muted'>Add filter</span>
            </Dropdown.Toggle>
            <Dropdown.Menu
              style={{width: '200px', minHeight: '100px', maxHeight: '300px', overflowY: 'auto'}}
            >
              <div className='mb-2'>
                <label
                  style={{
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '22.4px',
                    color: '#B0B7BD',
                  }}
                >
                  Select Column
                </label>
              </div>
              {defaultGroupField.map((col) => (
                <Dropdown.Item
                  key={col}
                  onClick={() => handleSelectColumn(col)}
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '19.6px',
                  }}
                >
                  {col}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* FilterPopover anchored to the dropdown button */}
          <FilterPopover
            show={showFilterPopover}
            tagOptions={tags}
            target={dropdownToggleRef.current}
            columnType={selectedColumn}
            includeTag={includeTagOrList}
            excludeTag={excludeTagOrList}
            containValue={containValue}
            statusValue={status}
            onStatusValueChange={setStatus}
            onClose={() => setShowFilterPopover(false)}
            onReset={handleResetPopover}
            onApply={handleApplyPopover}
            onIncludeTagChange={setIncludeTagOrList}
            onExcludeTagChange={setExcludeTagOrList}
            onContainValueChange={setContainValue}
          />

          <div
            className='d-flex flex-nowrap overflow-auto'
            style={{maxWidth: '100%'}}
            onWheel={(e) => {
              e.currentTarget.scrollLeft += e.deltaY
            }}
          >
            {filterTags.map((f, idx) => (
              <div key={idx} className='flex-shrink-0 me-2'>
                <FilterTag
                  column={f.column}
                  includeText={f.includeText}
                  excludeText={f.excludeText}
                  value={f.value}
                  onClose={() => removeFilter(f)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Filter
